import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { Spinner } from '@/components/ui/spinner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { KeyRound, Lock, AlertCircle, Trash2, Usb, Wifi, Bluetooth, Fingerprint, Smartphone, CheckCircle, ChevronDown } from 'lucide-react';
import { createAuthSession, getAuthSession } from '@/utils/auth-session';
import { ApplicationReturnLink } from '@/components/ApplicationReturnLink';
import { cn } from '@/lib/utils';

declare global {
  interface Window {
    google: {
      accounts: {
        id: {
          initialize: (config: unknown) => void;
          renderButton: (element: HTMLElement | null, config: unknown) => void;
          prompt: (callback?: (notification: {
            isNotDisplayed: () => boolean;
            isSkippedMoment: () => boolean;
            getNotDisplayedReason: () => string;
            getSkippedReason: () => string;
            getMomentType: () => string;
          }) => void) => void;
          disableAutoSelect: () => void;
        }
      }
    }
  }
}

interface StoredCredential {
  credentialId: string;
  transports: string[];
  createdAt?: string;
}

export default function Login() {
  const [loading, setLoading] = useState(false);
  const [storedCredentials, setStoredCredentials] = useState<StoredCredential[]>([]);
  const [selectedCredentialId, setSelectedCredentialId] = useState<string | null>(null);
  const [googleLoaded, setGoogleLoaded] = useState(false);
  const [showSecurityInfo, setShowSecurityInfo] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [applicationName, setApplicationName] = useState<string | null>(null);
  const [allowedTransports, setAllowedTransports] = useState<string[]>([]);
  const [googleAuthCompleted, setGoogleAuthCompleted] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userHasNoPasskeys, setUserHasNoPasskeys] = useState(false);
  const [hasLoadedUser, setHasLoadedUser] = useState(false);
  const [showPasskeyDropdown, setShowPasskeyDropdown] = useState(false);
  const [transportMismatchError, setTransportMismatchError] = useState<{
    allowedTransports: string[];
    passkeyTransports: string[];
  } | null>(null);
  const [showRedirectInterstitial, setShowRedirectInterstitial] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  // Get credentials from user object
  const getStoredCredentials = (): StoredCredential[] => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        return user.credentials || [];
      } catch (error) {
        console.error('Failed to parse stored user:', error);
      }
    }
    return [];
  };

  // Update credentials in user object and state
  const updateStoredCredentials = useCallback((credentials: StoredCredential[]) => {
    setStoredCredentials(credentials);
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        user.credentials = credentials;
        localStorage.setItem('user', JSON.stringify(user));
      } catch (error) {
        console.error('Failed to update user credentials:', error);
      }
    }
  }, []);


  const getTransportIcon = (transport: string) => {
    switch (transport) {
      case 'usb': return <Usb className="h-5 w-5" />;
      case 'nfc': return <Wifi className="h-5 w-5" />;
      case 'ble': return <Bluetooth className="h-5 w-5" />;
      case 'internal': return <Fingerprint className="h-5 w-5" />;
      case 'hybrid': return <Smartphone className="h-5 w-5" />;
      default: return <KeyRound className="h-5 w-5" />;
    }
  };

  const getTransportName = (transport: string) => {
    switch (transport) {
      case 'usb': return 'USB Security Key';
      case 'nfc': return 'NFC Security Key';
      case 'ble': return 'Bluetooth Security Key';
      case 'internal': return 'Platform Authenticator';
      case 'hybrid': return 'Phone as Security Key';
      default: return transport;
    }
  };

  const getTransportDescription = (transport: string) => {
    switch (transport) {
      case 'usb': return 'Physical key that plugs into your computer\'s USB port';
      case 'nfc': return 'Tap your key on NFC-enabled devices';
      case 'ble': return 'Wireless key that connects via Bluetooth';
      case 'internal': return 'Built-in biometric authentication (Touch ID, Face ID, Windows Hello)';
      case 'hybrid': return 'Use your phone to authenticate via QR code';
      default: return '';
    }
  };

  const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
    // Handle both base64 and base64url encoding
    const base64Standard = base64
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
    
    const binaryString = window.atob(base64Standard);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  };

  const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };

  const loginWithPasskey = useCallback(async (credentialId?: string) => {
    setLoading(true);

    try {
      // Use provided credential ID or the selected one
      const credentialToUse = credentialId || selectedCredentialId;
      
      if (!credentialToUse) {
        throw new Error('No credential selected. Please choose a passkey.');
      }

      // Get authentication options from server
      const optionsResponse = await fetch('https://identity.tools.agentic.inv.tech/passkey/authenticate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          credentialId: credentialToUse
        })
      });

      if (!optionsResponse.ok) {
        const errorData = await optionsResponse.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to get authentication options');
      }

      const options = await optionsResponse.json();

      // Check if the server returned an error
      if (options.success === false) {
        throw new Error(options.error || 'Failed to get authentication options');
      }

      // Convert base64 strings to ArrayBuffers
      options.publicKey.challenge = base64ToArrayBuffer(options.publicKey.challenge);
      if (options.publicKey.allowCredentials) {
        options.publicKey.allowCredentials = options.publicKey.allowCredentials.map((cred: { id: string; type: string; transports?: string[] }) => ({
          ...cred,
          id: base64ToArrayBuffer(cred.id),
          transports: cred.transports
        }));
      }

      const verifyToast = toast({
        title: "Verify your identity",
        description: "Touch your security key or use your device's biometric authentication",
      });

      // Get assertion from authenticator
      const assertion = await navigator.credentials.get(options) as PublicKeyCredential;
      const assertionResponse = assertion.response as AuthenticatorAssertionResponse;
      
      // Dismiss the verify toast after WebAuthn completes
      verifyToast.dismiss();

      // Get auth session
      const session = getAuthSession();
      if (!session) {
        throw new Error('No active authentication session');
      }
      
      // Send assertion to server for verification with session info
      const verifyResponse = await fetch('https://identity.tools.agentic.inv.tech/auth/passkey', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          credentialId: assertion.id,
          clientDataJSON: arrayBufferToBase64(assertionResponse.clientDataJSON),
          authenticatorData: arrayBufferToBase64(assertionResponse.authenticatorData),
          signature: arrayBufferToBase64(assertionResponse.signature),
          userHandle: assertionResponse.userHandle ? arrayBufferToBase64(assertionResponse.userHandle) : null,
          session_id: session.sessionId
        })
      });

      const result = await verifyResponse.json();

      if (!result.success) {
        // Check for transport mismatch error
        if (result.error === 'transport_mismatch') {
          setTransportMismatchError({
            allowedTransports: result.allowed_transports || [],
            passkeyTransports: result.passkey_transports || []
          });
          const error = new Error(result.error_description || 'Transport mismatch') as Error & {
            isTransportMismatch: boolean;
            allowedTransports?: string[];
          };
          error.isTransportMismatch = true;
          error.allowedTransports = result.allowed_transports;
          throw error;
        }
        // Check for user status errors
        if (result.error === 'user_not_found' || result.error === 'user_inactive') {
          const error = new Error(result.error_description || result.error) as Error & {
            isUserStatusError: boolean;
          };
          error.isUserStatusError = true;
          throw error;
        }
        throw new Error(result.error_description || result.error || 'Authentication failed');
      }

      // The /auth/passkey endpoint returns a redirect URL with authorization code
      if (result.redirectUrl) {
        // Store credentials if returned
        if (result.credentials && result.credentials.length > 0) {
          updateStoredCredentials(result.credentials);
        }
        
        // Store the access token if provided (for delete functionality)
        if (result.access_token) {
          sessionStorage.setItem('access_token', result.access_token);
        }
        
        // Show interstitial animation
        setShowRedirectInterstitial(true);
        
        // Clear stored application info before redirect
        sessionStorage.removeItem('application_name');
        sessionStorage.removeItem('application_url');
        
        // The redirectUrl already contains the authorization code and state
        // The receiving application will exchange the code for tokens via /token endpoint
        setTimeout(() => {
          window.location.href = result.redirectUrl;
        }, 1500);
      } else {
        throw new Error('No redirect URL received');
      }

    } catch (err) {
      console.error('Passkey authentication error:', err);

      let errorMessage = 'Authentication failed';
      let errorDescription = 'Please try again';
      
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          errorDescription = 'Operation cancelled or timed out';
        } else if (err.message.includes('No passkey found')) {
          errorMessage = 'No passkey found';
          errorDescription = err.message;
        } else if (err.message === 'Credential not found') {
          // Remove the invalid credential from localStorage
          const currentCredentialId = credentialId || selectedCredentialId;
          if (currentCredentialId) {
            const updatedCreds = storedCredentials.filter(c => c.credentialId !== currentCredentialId);
            updateStoredCredentials(updatedCreds);
          }
          errorDescription = 'Your passkey was not found. Please sign in with Google to set up a new passkey.';
        } else if ((err as Error & { isTransportMismatch?: boolean }).isTransportMismatch) {
          // Don't show toast for transport mismatch - we'll show on-page error
          return;
        } else if ((err as Error & { isUserStatusError?: boolean }).isUserStatusError) {
          errorMessage = 'Account Status Error';
          errorDescription = err.message;
        } else {
          errorDescription = err.message;
        }
      }

      toast({
        title: errorMessage,
        description: errorDescription,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast, selectedCredentialId, updateStoredCredentials, storedCredentials]);

  const performPasskeyDeletion = useCallback(async (credentialIdToDelete?: string) => {
    setLoading(true);
    
    try {
      // Use provided credential ID or the selected one
      const credentialId = credentialIdToDelete || selectedCredentialId;
      if (!credentialId) {
        toast({
          title: "No passkey selected",
          description: "Please select a passkey to delete",
          variant: "destructive",
        });
        return;
      }

      // Get the fresh Google ID token
      const googleIdToken = sessionStorage.getItem('google_id_token');
      if (!googleIdToken) {
        throw new Error('No Google authentication token found');
      }

      // Call the delete endpoint with Google ID token
      const response = await fetch('https://identity.tools.agentic.inv.tech/passkey/delete', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          credentialId,
          id_token: googleIdToken
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to delete passkey' }));
        throw new Error(error.error_description || error.error || 'Failed to delete passkey');
      }

      const result = await response.json();
      
      if (result.success) {
        // Remove the deleted credential from localStorage
        const updatedCreds = storedCredentials.filter(c => c.credentialId !== credentialId);
        updateStoredCredentials(updatedCreds);
        
        // Clear stored tokens
        sessionStorage.removeItem('google_id_token');
        sessionStorage.removeItem('pending_passkey_delete');
        sessionStorage.removeItem('delete_from_transport_mismatch');
        
        // Clear transport mismatch error if present
        setTransportMismatchError(null);
        
        // Reset Google auth state to show the Google Login button again
        setGoogleAuthCompleted(false);
        setUserHasNoPasskeys(false);
        
        toast({
          title: "Passkey deleted",
          description: "Your passkey has been successfully deleted. You'll need to set up a new one after signing in with Google.",
        });
      } else {
        throw new Error('Failed to delete passkey');
      }
      
    } catch (error) {
      console.error('Delete passkey error:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete passkey",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  }, [toast, selectedCredentialId, updateStoredCredentials, storedCredentials]);

  const handleGoogleCredentialResponse = useCallback(async (response: { credential?: string }) => {
    if (!response.credential) {
      toast({
        title: "Authentication failed",
        description: "No credential received. Please try again.",
        variant: "destructive",
      });
      return;
    }

    // Store the Google ID token for potential passkey deletion
    sessionStorage.setItem('google_id_token', response.credential);

    // Check if we're in delete mode
    const pendingDelete = sessionStorage.getItem('pending_passkey_delete');
    
    if (pendingDelete === 'true') {
      sessionStorage.removeItem('pending_passkey_delete');
      sessionStorage.removeItem('delete_from_transport_mismatch');
      
      // We have a fresh token, perform the deletion immediately
      setTimeout(() => {
        performPasskeyDeletion();
      }, 100); // Small delay to ensure token is stored
      return;
    }

    try {
      setLoading(true);

      // Get auth session
      const session = getAuthSession();
      if (!session) {
        throw new Error('No active authentication session');
      }
      
      // Send Google ID token to OIDC service with session info
      const result = await fetch('https://identity.tools.agentic.inv.tech/auth/google', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id_token: response.credential,
          session_id: session.sessionId
        })
      });

      if (!result.ok) {
        const errorText = await result.text();
        console.error('OIDC error:', errorText);
        throw new Error('Authentication failed');
      }

      const data = await result.json();

      // Check if authentication was successful
      if (data.success === false) {
        toast({
          title: "Access Denied",
          description: data.error,
          variant: "destructive",
        });
        return;
      }

      if (data.user) {
        // Store user info in localStorage
        localStorage.setItem('user', JSON.stringify(data.user));
        
        // Store user info for passkey flow
        sessionStorage.setItem('user_id', data.user.id);
        
        // Get redirect URL and session ID from session
        const authSession = getAuthSession();
        if (!authSession || !authSession.redirectUri) {
          throw new Error('No valid authentication session found');
        }
        const redirectTo = authSession.redirectUri;
        sessionStorage.setItem('auth_redirect', redirectTo);
        
        // Store session ID for passkey registration
        if (authSession?.sessionId) {
          sessionStorage.setItem('session_id', authSession.sessionId);
        }
        
        // Check if passkey setup is required
        if (data.requires_passkey_setup === true) {
          // User has no passkeys
          toast({
            title: "Welcome!",
            description: "Authentication successful",
          });
          
          // Mark Google auth as completed and show create passkey option
          setGoogleAuthCompleted(true);
          setUserHasNoPasskeys(true);
        } else {
          // User has passkeys - store them from user object
          toast({
            title: "Welcome back!",
            description: "Authentication successful",
          });
          
          // Mark Google auth as completed
          setGoogleAuthCompleted(true);
          setUserHasNoPasskeys(false);
          
          // Update stored credentials state
          if (data.user.credentials && data.user.credentials.length > 0) {
            updateStoredCredentials(data.user.credentials);
            // Auto-select the first credential if only one exists
            if (data.user.credentials.length === 1) {
              setSelectedCredentialId(data.user.credentials[0].credentialId);
            }
          }
        }
      } else {
        throw new Error('Invalid response from server');
      }

    } catch (error) {
      console.error('Error processing credential:', error);
      toast({
        title: "Authentication failed",
        description: "Failed to process login. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast, performPasskeyDeletion, updateStoredCredentials]);


  useEffect(() => {
    // Fire parallel requests to warm up Lambda functions
    const warmUpLambdas = async () => {
      try {
        await Promise.all([
          // Well-known endpoint
          fetch('https://identity.tools.agentic.inv.tech/.well-known/openid-configuration', {
            method: 'GET',
            headers: { 'Accept': 'application/json' }
          }).catch(() => {}), // Ignore errors for warm-up requests
          
          // JWKS endpoint
          fetch('https://identity.tools.agentic.inv.tech/.well-known/jwks.json', {
            method: 'GET',
            headers: { 'Accept': 'application/json' }
          }).catch(() => {}),
          
          // Authorize endpoint with minimal params (will return error but warms up Lambda)
          fetch('https://identity.tools.agentic.inv.tech/authorize?client_id=warm-up&response_type=code', {
            method: 'GET',
            headers: { 'Accept': 'application/json' }
          }).catch(() => {}),
          
          // Token endpoint warm-up
          fetch('https://identity.tools.agentic.inv.tech/token', {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ grant_type: 'warm-up' })
          }).catch(() => {})
        ]);
      } catch (error) {
        // Silently ignore warm-up errors
      }
    };
    
    warmUpLambdas();
    
    // Load persisted application info from sessionStorage
    const storedAppName = sessionStorage.getItem('application_name');
    if (storedAppName) setApplicationName(storedAppName);
    
    // Check for origin error from authorize endpoint
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');
    
    if (error === 'invalid_origin' && errorDescription) {
      setAuthError(errorDescription);
      return;
    }
    
    // Check if this is a session-based login (OAuth parameters stored server-side)
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      // This is a valid session-based login, create a minimal session object
      createAuthSession(searchParams);
      // No error handling needed - session_id means OAuth params are server-side
      
      // Fetch application name
      fetch(`https://identity.tools.agentic.inv.tech/session/info?session_id=${sessionId}`)
        .then(async response => {
          const data = await response.json();
          if (!data.success) {
            // Session error - show it to the user
            console.error('Session info error:', data);
            setSessionError(data.error_description || 'Session error occurred');
            return;
          }
          if (data.application_name) {
            setApplicationName(data.application_name);
            // Store in sessionStorage for persistence
            sessionStorage.setItem('application_name', data.application_name);
          }
          if (data.application_url) {
            // Store in sessionStorage for persistence
            sessionStorage.setItem('application_url', data.application_url);
          }
          if (data.allowed_transports) {
            setAllowedTransports(data.allowed_transports);
            // Store in sessionStorage for persistence
            sessionStorage.setItem('allowed_transports', JSON.stringify(data.allowed_transports));
          }
        })
        .catch(error => {
          console.error('Failed to fetch session info:', error);
        });
    } else {
      // Traditional OAuth flow - validate parameters
      const clientId = searchParams.get('client_id');
      const redirectUri = searchParams.get('redirect_uri');
      
      // Check if using legacy redirect parameter (not allowed)
      const legacyRedirect = searchParams.get('redirect');
      if (legacyRedirect) {
        setAuthError('Invalid request: Applications must use the /authorize endpoint with proper OAuth parameters (client_id, redirect_uri, response_type). Direct access to /client is not allowed.');
        return;
      }
      
      // Validate required OAuth parameters
      if (!clientId || !redirectUri) {
        // Check if we have stored application info (e.g., user navigated back)
        const storedAppName = sessionStorage.getItem('application_name');
        const storedAppUrl = sessionStorage.getItem('application_url');
        
        if (storedAppName && storedAppUrl) {
          // User likely navigated back, we have app info, so don't show error
          console.log('Using stored application info from previous session');
        } else {
          setAuthError('Invalid request: Missing required OAuth parameters. Applications must use the /authorize endpoint with client_id and redirect_uri.');
          return;
        }
      }
      
      // Create OAuth session only if we have parameters
      if (clientId && redirectUri) {
        const session = createAuthSession(searchParams);
        if (!session) {
          setAuthError('Invalid request: Failed to create authentication session. Please ensure all required OAuth parameters are provided.');
          return;
        }
      }
    }
    
    // This credential loading logic is moved to a separate effect

    // Initialize Google Sign-In
    const initializeGoogleSignIn = () => {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: '1090194705577-nkk9e9m6b886ruopv8gq9smb19jc1s3o.apps.googleusercontent.com',
          callback: handleGoogleCredentialResponse,
          auto_select: false,
          context: 'signin',
          ux_mode: 'popup'
        });
        setGoogleLoaded(true);
      }
    };

    // Wait for Google script to load
    const checkGoogleLoaded = setInterval(() => {
      if (window.google) {
        clearInterval(checkGoogleLoaded);
        initializeGoogleSignIn();
      }
    }, 100);

    // Cleanup
    const timeoutId = setTimeout(() => {
      clearInterval(checkGoogleLoaded);
      if (!window.google) {
        toast({
          title: "Google Sign-In unavailable",
          description: "Please check your internet connection",
          variant: "destructive",
        });
      }
    }, 5000);

    return () => {
      clearInterval(checkGoogleLoaded);
      clearTimeout(timeoutId);
    };
  }, [toast, handleGoogleCredentialResponse, searchParams]);

  // Effect for initial setup - runs only once
  useEffect(() => {
    const credentials = getStoredCredentials();
    setStoredCredentials(credentials);
    // Auto-select if only one credential
    if (credentials.length === 1) {
      setSelectedCredentialId(credentials[0].credentialId);
    }
    setHasLoadedUser(true);
  }, []); // Empty dependency array - runs only once on mount

  const handleGoogleSignIn = () => {
    if (window.google) {
      // Clear any existing session data to force new authentication
      sessionStorage.removeItem('google_id_token');
      sessionStorage.removeItem('user_id');
      
      // Create a temporary div to render the Google button
      const googleButtonDiv = document.createElement('div');
      googleButtonDiv.style.position = 'absolute';
      googleButtonDiv.style.left = '-9999px';
      googleButtonDiv.id = 'google-signin-button-temp';
      document.body.appendChild(googleButtonDiv);
      
      // Re-initialize and render button
      window.google.accounts.id.initialize({
        client_id: '1090194705577-nkk9e9m6b886ruopv8gq9smb19jc1s3o.apps.googleusercontent.com',
        callback: handleGoogleCredentialResponse,
        auto_select: false,
        context: 'signin',
        ux_mode: 'popup'
      });
      
      // Render the Google Sign-In button
      window.google.accounts.id.renderButton(
        googleButtonDiv,
        {
          theme: 'filled_black',
          size: 'large',
          type: 'standard',
          shape: 'rectangular',
          text: 'signin_with',
          logo_alignment: 'left'
        }
      );
      
      // Trigger click on the rendered button
      setTimeout(() => {
        const button = googleButtonDiv.querySelector('div[role="button"]') as HTMLElement;
        if (button) {
          button.click();
        }
        // Clean up
        setTimeout(() => {
          if (document.body.contains(googleButtonDiv)) {
            document.body.removeChild(googleButtonDiv);
          }
        }, 100);
      }, 10);
    }
  };

  const handleCreatePasskey = () => {
    // Clear any transport mismatch error when creating new passkey
    setTransportMismatchError(null);
    // Get user ID from session storage
    const userId = sessionStorage.getItem('user_id');
    if (userId) {
      navigate(`/passkey/setup?user=${userId}`);
    } else {
      toast({
        title: "Error",
        description: "User information not found. Please sign in again.",
        variant: "destructive",
      });
    }
  };


  const handleDeletePasskey = async () => {
    // Always require fresh authentication for security
    // Google ID tokens expire after 1 hour
    toast({
      title: "Authentication required",
      description: "Please sign in with Google to confirm passkey deletion",
      variant: "default",
    });
    
    // Close the dialog if open
    setShowDeleteConfirm(false);
    
    // Store a flag to indicate we're in delete mode
    sessionStorage.setItem('pending_passkey_delete', 'true');
    
    // Clear any existing token to force fresh authentication
    sessionStorage.removeItem('google_id_token');
    
    // Trigger Google sign-in
    handleGoogleSignIn();
  };

  return (
    <AuthLayout applicationName={applicationName || undefined}>
      {authError && (
        <div className="w-screen relative left-[50%] right-[50%] ml-[-50vw] mr-[-50vw] px-4 mb-6">
          <Alert variant="destructive" className="max-w-4xl mx-auto">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <AlertTitle>Authentication Error</AlertTitle>
            <AlertDescription className="space-y-2">
              <p className="break-words">{authError}</p>
              {searchParams.get('redirect') && (
                <div className="mt-3 p-3 bg-destructive/10 rounded-md">
                  <p className="font-semibold text-sm mb-1">Issues detected:</p>
                  <ol className="list-decimal list-inside text-sm space-y-1">
                    <li>The application should not redirect to /client</li>
                    <li>The client_id is not being passed in</li>
                  </ol>
                </div>
              )}
              <div className="mt-3 text-sm">
                <p className="font-semibold">Correct usage:</p>
                <code className="block mt-1 p-2 bg-muted rounded text-xs overflow-x-auto">
                  /authorize?client_id=YOUR_APP_ID&redirect_uri=YOUR_REDIRECT_URL&response_type=code
                </code>
              </div>
            </AlertDescription>
          </Alert>
          <ApplicationReturnLink />
        </div>
      )}
      
      {transportMismatchError ? (
        // Full width transport mismatch error display
        <div className="w-screen relative left-[50%] right-[50%] ml-[-50vw] mr-[-50vw] px-4">
          <div className="max-w-[80vw] mx-auto space-y-6">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Security Key Type Not Allowed</AlertTitle>
              <AlertDescription>
                Your passkey doesn't match the security requirements for this application.
              </AlertDescription>
            </Alert>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border-green-200 bg-green-50/50 dark:bg-green-950/20 dark:border-green-800">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-medium text-green-700 dark:text-green-400">Required Security Keys</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {transportMismatchError.allowedTransports.map((transport) => (
                    <div key={transport} className="flex items-start gap-3">
                      <div className="text-green-600 dark:text-green-400">
                        {getTransportIcon(transport)}
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-medium">{getTransportName(transport)}</p>
                        <p className="text-sm text-muted-foreground">{getTransportDescription(transport)}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
              
              <Card className="border-red-200 bg-red-50/50 dark:bg-red-950/20 dark:border-red-800">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-medium text-red-700 dark:text-red-400">Your Current Passkey</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {transportMismatchError.passkeyTransports.length > 0 ? (
                    transportMismatchError.passkeyTransports.map((transport) => (
                      <div key={transport} className="flex items-start gap-3">
                        <div className="text-red-600 dark:text-red-400">
                          {getTransportIcon(transport)}
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm font-medium">{getTransportName(transport)}</p>
                          <p className="text-sm text-muted-foreground">{getTransportDescription(transport)}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">No transport information available</p>
                  )}
                </CardContent>
              </Card>
            </div>
            
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>What to do next</AlertTitle>
              <AlertDescription className="space-y-2">
                <p>You have two options:</p>
                <ol className="list-decimal list-inside space-y-1 ml-2">
                  <li>Delete your current passkey and create a new one that matches the requirements</li>
                  <li>Contact your administrator to update the security requirements</li>
                </ol>
              </AlertDescription>
            </Alert>
            
            <div className="flex gap-3 max-w-md mx-auto">
              <Button
                onClick={() => {
                  // Always require fresh auth for deletion
                  toast({
                    title: "Authentication required",
                    description: "Please sign in with Google to delete your passkey",
                    variant: "default",
                  });
                  
                  // Store flags to indicate we're in delete mode from transport mismatch
                  sessionStorage.setItem('pending_passkey_delete', 'true');
                  sessionStorage.setItem('delete_from_transport_mismatch', 'true');
                  
                  // Clear any existing token to force fresh authentication
                  sessionStorage.removeItem('google_id_token');
                  
                  // Trigger Google sign-in
                  handleGoogleSignIn();
                }}
                variant="destructive"
                className="flex-1"
                disabled={loading}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Current Passkey
              </Button>
              <Button
                onClick={() => setTransportMismatchError(null)}
                variant="outline"
                className="flex-1"
              >
                Try Again
              </Button>
            </div>
            
            <ApplicationReturnLink />
          </div>
        </div>
      ) : (
      <Card className="w-full animate-in fade-in-50 duration-500">
        <CardHeader className="space-y-1 relative">
          <CardTitle className="text-2xl font-semibold tracking-tight">
            Sign in
          </CardTitle>
          <button
            onClick={() => setShowSecurityInfo(true)}
            className="absolute top-4 right-4 p-2 rounded-lg hover:bg-muted transition-colors"
            aria-label="Security information"
          >
            <Lock className="h-4 w-4 text-muted-foreground" />
          </button>
        </CardHeader>

        <CardContent className="space-y-4">
          {authError ? (
            <div className="text-center text-sm text-muted-foreground">
              Please see the error message above for details.
            </div>
          ) : sessionError ? (
            <>
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Session Error</AlertTitle>
                <AlertDescription>{sessionError}</AlertDescription>
              </Alert>
              <ApplicationReturnLink />
            </>
          ) : transportMismatchError ? (
            // This case is handled outside the card
            null
          ) : (
            <>
              {/* Show passkey button with dropdown on initial load if credentials exist */}
              {!googleAuthCompleted && hasLoadedUser && storedCredentials.length > 0 && (
                <div className="space-y-4">
                  {storedCredentials.length === 1 ? (
                    <Button 
                      onClick={() => loginWithPasskey(storedCredentials[0].credentialId)} 
                      className="w-full" 
                      disabled={loading}
                      size="lg"
                      variant="default"
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="mr-2" />
                          Authenticating...
                        </>
                      ) : (
                        <>
                          {getTransportIcon(storedCredentials[0].transports[0] || 'usb')}
                          <span className="ml-2">Login with {getTransportName(storedCredentials[0].transports[0] || 'usb')}</span>
                        </>
                      )}
                    </Button>
                  ) : (
                    <DropdownMenu open={showPasskeyDropdown} onOpenChange={setShowPasskeyDropdown}>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          className="w-full" 
                          disabled={loading}
                          size="lg"
                          variant="default"
                        >
                          <KeyRound className="mr-2 h-5 w-5" />
                          Login with Passkey
                          <ChevronDown className="ml-2 h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-full" align="center">
                        {storedCredentials.map((cred) => (
                          <DropdownMenuItem
                            key={cred.credentialId}
                            onClick={() => {
                              setShowPasskeyDropdown(false);
                              loginWithPasskey(cred.credentialId);
                            }}
                            className="cursor-pointer"
                          >
                            <div className="flex items-center gap-3 w-full py-1">
                              <div className="text-muted-foreground">
                                {getTransportIcon(cred.transports[0] || 'usb')}
                              </div>
                              <div className="flex-1">
                                <p className="text-sm font-medium">{getTransportName(cred.transports[0] || 'usb')}</p>
                                {cred.createdAt && (
                                  <p className="text-xs text-muted-foreground">
                                    Added {new Date(cred.createdAt).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                            </div>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              )}
              
              {(storedCredentials.length > 0 || googleAuthCompleted) && googleAuthCompleted && (
                <>
                  {storedCredentials.length > 0 && (
                <>
                  {/* Show credential selection if multiple credentials */}
                  {storedCredentials.length > 1 && (
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">Select a passkey to sign in:</p>
                      <div className="space-y-2">
                        {storedCredentials.map((cred) => (
                          <div
                            key={cred.credentialId}
                            className={cn(
                              "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all",
                              selectedCredentialId === cred.credentialId
                                ? "border-primary bg-primary/5"
                                : "border-border hover:border-primary/50"
                            )}
                            onClick={() => setSelectedCredentialId(cred.credentialId)}
                          >
                            <input
                              type="radio"
                              name="credential"
                              value={cred.credentialId}
                              checked={selectedCredentialId === cred.credentialId}
                              onChange={() => setSelectedCredentialId(cred.credentialId)}
                              className="h-4 w-4"
                            />
                            <div className="flex items-center gap-2 flex-1">
                              {cred.transports.map((transport) => (
                                <div key={transport} className="text-muted-foreground">
                                  {getTransportIcon(transport)}
                                </div>
                              ))}
                              <span className="text-sm">{getTransportName(cred.transports[0] || 'unknown')}</span>
                            </div>
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedCredentialId(cred.credentialId);
                                setShowDeleteConfirm(true);
                              }}
                              size="sm"
                              variant="ghost"
                              className="p-1"
                              disabled={loading}
                              title="Delete this passkey"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => loginWithPasskey()} 
                      className="flex-1" 
                      disabled={loading || !selectedCredentialId}
                      size="lg"
                      variant="default"
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="mr-2" />
                          Authenticating...
                        </>
                      ) : (
                        <>
                          <KeyRound className="mr-2 h-5 w-5" />
                          Continue with Passkey
                        </>
                      )}
                    </Button>
                    {storedCredentials.length === 1 && (
                      <Button
                        onClick={() => setShowDeleteConfirm(true)}
                        size="lg"
                        variant="outline"
                        className="px-3"
                        disabled={loading}
                        title="Delete passkey"
                      >
                        <Trash2 className="h-5 w-5" />
                      </Button>
                    )}
                  </div>

                </>
              )}

                  {googleAuthCompleted && userHasNoPasskeys && (
                <>
                  <Button 
                    onClick={handleCreatePasskey} 
                    className="w-full" 
                    disabled={loading}
                    size="lg"
                    variant="default"
                  >
                    <KeyRound className="mr-2 h-5 w-5" />
                    Create a Passkey
                  </Button>
                  
                  {allowedTransports.length > 0 && (
                    <Alert className="mt-4">
                      <AlertCircle className="h-4 w-4" />
                      <AlertTitle>Supported Security Key Types</AlertTitle>
                      <AlertDescription>
                        <div className="mt-2 space-y-2">
                          <p className="text-sm font-medium">This application requires one of the following security key types:</p>
                          <div className="space-y-3">
                            {allowedTransports.includes('usb') && (
                              <div className="flex items-start gap-3">
                                <Usb className="h-5 w-5 text-muted-foreground mt-0.5" />
                                <div className="space-y-1">
                                  <p className="text-sm font-medium">USB Security Key</p>
                                  <p className="text-sm text-muted-foreground">Physical key that plugs into your computer's USB port</p>
                                </div>
                              </div>
                            )}
                            {allowedTransports.includes('nfc') && (
                              <div className="flex items-start gap-3">
                                <Wifi className="h-5 w-5 text-muted-foreground mt-0.5" />
                                <div className="space-y-1">
                                  <p className="text-sm font-medium">NFC Security Key</p>
                                  <p className="text-sm text-muted-foreground">Tap your key on NFC-enabled devices</p>
                                </div>
                              </div>
                            )}
                            {allowedTransports.includes('ble') && (
                              <div className="flex items-start gap-3">
                                <Bluetooth className="h-5 w-5 text-muted-foreground mt-0.5" />
                                <div className="space-y-1">
                                  <p className="text-sm font-medium">Bluetooth Security Key</p>
                                  <p className="text-sm text-muted-foreground">Wireless key that connects via Bluetooth</p>
                                </div>
                              </div>
                            )}
                            {allowedTransports.includes('internal') && (
                              <div className="flex items-start gap-3">
                                <Fingerprint className="h-5 w-5 text-muted-foreground mt-0.5" />
                                <div className="space-y-1">
                                  <p className="text-sm font-medium">Platform Authenticator</p>
                                  <p className="text-sm text-muted-foreground">Built-in biometric authentication (Touch ID, Face ID, Windows Hello)</p>
                                </div>
                              </div>
                            )}
                            {allowedTransports.includes('hybrid') && (
                              <div className="flex items-start gap-3">
                                <Smartphone className="h-5 w-5 text-muted-foreground mt-0.5" />
                                <div className="space-y-1">
                                  <p className="text-sm font-medium">Phone as Security Key</p>
                                  <p className="text-sm text-muted-foreground">Use your phone to authenticate via QR code</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}
                </>
              )}
                </>
              )}

              {!googleAuthCompleted && storedCredentials.length === 0 && (
            <div className="flex justify-center w-full">
              {!googleLoaded && !loading ? (
                <div className="flex items-center justify-center h-11 w-full rounded-md border border-input bg-background">
                  <Spinner size="sm" className="mr-2" />
                  <span className="text-sm text-muted-foreground">Loading...</span>
                </div>
              ) : (
                <Button
                  onClick={handleGoogleSignIn}
                  disabled={loading || !googleLoaded}
                  size="lg"
                  className="w-full bg-black hover:bg-gray-800 text-white border-0 font-medium dark:bg-white dark:hover:bg-gray-200 dark:text-black"
                >
                <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Google Login
              </Button>
              )}
              </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      )}
      
      {/* Show application link at the bottom of the page if no major errors */}
      {!authError && !transportMismatchError && !sessionError && (
        <ApplicationReturnLink alwaysShowSpace />
      )}

      <Dialog open={showSecurityInfo} onOpenChange={setShowSecurityInfo}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Enhanced Security
            </DialogTitle>
            <DialogDescription className="pt-3 space-y-3">
              <p>
                Your security is our top priority. Here's how we protect your account:
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-0.5">•</span>
                  <span>After signing in with Google, you'll set up a passkey for secure, passwordless authentication</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-0.5">•</span>
                  <span>Passkeys are stored on your physical security key, not on our servers</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-0.5">•</span>
                  <span>Your private key never leaves your security device</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-0.5">•</span>
                  <span>Protection against phishing and password breaches</span>
                </li>
              </ul>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Delete Passkey
            </DialogTitle>
            <DialogDescription className="pt-3 space-y-3">
              <p className="text-destructive">
                Are you sure you want to delete this passkey? This action cannot be undone.
              </p>
              <p>
                You will need to set up a new passkey after signing in with Google again.
              </p>
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeletePasskey}
              className="flex-1"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Deleting...
                </>
              ) : (
                'Delete Passkey'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Redirect Interstitial */}
      {showRedirectInterstitial && (
        <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="animate-in fade-in-0 zoom-in-95 duration-300">
            <Card className="w-[400px] shadow-lg">
              <CardContent className="pt-6">
                <div className="flex flex-col items-center space-y-4">
                  {/* Success icon with animation */}
                  <div className="relative">
                    <div className="absolute inset-0 animate-ping">
                      <CheckCircle className="h-16 w-16 text-green-500 opacity-75" />
                    </div>
                    <CheckCircle className="h-16 w-16 text-green-500 relative animate-in zoom-in-50 duration-500" />
                  </div>
                  
                  {/* Success message */}
                  <div className="text-center space-y-2 animate-in fade-in-50 slide-in-from-bottom-3 duration-500 delay-200">
                    <h3 className="text-lg font-semibold">Authentication Successful!</h3>
                    <p className="text-sm text-muted-foreground">
                      Redirecting you to {applicationName || 'the application'}...
                    </p>
                  </div>
                  
                  {/* Loading spinner */}
                  <div className="flex items-center space-x-2 animate-in fade-in-50 duration-500 delay-300">
                    <Spinner size="sm" />
                    <span className="text-sm text-muted-foreground">Please wait...</span>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="w-full bg-secondary rounded-full h-2 overflow-hidden animate-in fade-in-50 duration-500 delay-400">
                    <div className="h-full bg-primary rounded-full animate-[progress_1.5s_ease-in-out]" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </AuthLayout>
  );
}